import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { num, pct } from "@owners/format";
import { api } from "../api";

/**
 * People & training (Employee Workshop feed, docs/WORKSHOP_FEED.md) — on
 * Today's Update. Portfolio line + one row per hotel for the chosen month.
 * Aggregates only (no names); hotels the Workshop has no data for are listed
 * under the table. Shows nothing at all until the feed is connected.
 */

interface Metric { key: string; label: string; unit: "pct" | "count" | "cents" | "score" | "hours" | "days"; value: number | null }
interface Res { connected: boolean; month: string; hotels: Array<{ id: string; name: string; asOf: string | null; metrics: Metric[] | null }> }

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };
const monthName = (m: string) => new Date(m + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

/** The columns shown per hotel, in order. Anything else the feed sends stays available in the hotel's detail row. */
const COLS: Array<[string, string]> = [
  ["training_enrolled", "Enrolled"],
  ["training_completion_rate", "Completion"],
  ["training_modules_completed_in_month", "Modules this month"],
  ["training_avg_quiz_score", "Quiz avg"],
  ["safety_training_completion_rate", "Safety training"],
  ["certifications_expiring_60_days", "Certs expiring 60 d"],
  ["scores_brand_qa_avg", "Brand QA"],
  ["scores_oci_avg", "OCI audit"],
  ["coaching_one_on_ones_in_month", "1:1s this month"],
];

const fmt = (m: Metric | undefined) =>
  !m || m.value === null ? "—" : m.unit === "pct" ? pct(m.value, { ratio: true }) : m.unit === "score" ? m.value.toFixed(1) : num(m.value);

export function PeopleTraining({ endpoint = "/api/workshop", title = "People & training", showWhenOff = false }: { endpoint?: string; title?: string; showWhenOff?: boolean } = {}) {
  const [month, setMonth] = useState(lastMonth());
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["workshop", endpoint, month], queryFn: () => api<Res>(`${endpoint}?hotelIds=all&month=${month}`) });
  const d = q.data;
  if (q.isLoading && showWhenOff) return <div className="card muted">Loading…</div>;
  if (!d || !d.connected) {                                   // not connected yet → hidden on Today's Update, a quiet note elsewhere
    return showWhenOff ? <div className="card"><h2 style={{ marginTop: 0 }}>{title}</h2><p className="muted">The Employee Workshop isn't connected yet.</p></div> : null;
  }

  const withData = d.hotels.filter((h) => h.metrics && h.metrics.length);
  const without = d.hotels.filter((h) => !h.metrics || !h.metrics.length);
  const get = (h: Res["hotels"][number], k: string) => h.metrics?.find((m) => m.key === k);
  const val = (h: Res["hotels"][number], k: string) => get(h, k)?.value ?? null;
  const sum = (k: string) => withData.reduce((t, h) => t + (val(h, k) ?? 0), 0);
  const mean = (k: string) => { const v = withData.map((h) => val(h, k)).filter((x): x is number => x !== null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };

  // Portfolio completion weighted by enrolment; safety training by required / completed counts.
  const enrolled = sum("training_enrolled");
  const weighted = withData.reduce((t, h) => t + (val(h, "training_enrolled") ?? 0) * (val(h, "training_completion_rate") ?? 0), 0);
  const completion = enrolled ? weighted / enrolled : null;
  const safetyReq = sum("safety_training_required"), safetyDone = sum("safety_training_completed");

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <label className="muted">Month <input type="month" value={month} max={lastMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} /></label>
      </div>
      {q.isFetching && !withData.length ? <p className="muted">Loading…</p> : withData.length === 0 ? (
        <p className="muted">No training data for {monthName(d.month)} yet.</p>
      ) : (
        <>
          <p style={{ marginTop: 8 }}>
            {monthName(d.month)}: <strong>{num(enrolled)}</strong> staff enrolled across {num(withData.length)} hotels,
            {" "}<strong>{pct(completion, { ratio: true })}</strong> training completion,
            {" "}{num(sum("training_modules_completed_in_month"))} modules completed in the month
            {mean("training_avg_quiz_score") !== null && <>, quiz average {pct(mean("training_avg_quiz_score"), { ratio: true })}</>}.
            {safetyReq > 0 && <> Safety training {num(safetyDone)} of {num(safetyReq)} done.</>}
            {sum("certifications_expiring_60_days") > 0 && <> {num(sum("certifications_expiring_60_days"))} certifications expire within 60 days.</>}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="fin">
              <thead><tr><th>Hotel</th>{COLS.map(([, l]) => <th key={l} className="num">{l}</th>)}</tr></thead>
              <tbody>
                {withData.map((h) => (
                  <Fragment key={h.id}>
                    <tr onClick={() => setOpen(open === h.id ? null : h.id)} style={{ cursor: "pointer" }}>
                      <td>{h.name}</td>
                      {COLS.map(([k]) => <td key={k} className="num">{fmt(get(h, k))}</td>)}
                    </tr>
                    {open === h.id && (
                      <tr><td colSpan={COLS.length + 1} className="muted" style={{ fontSize: 12 }}>
                        {h.metrics!.map((m) => `${m.label}: ${fmt(m)}`).join(" · ")}
                        {h.asOf && <> · as of {new Date(h.asOf).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</>}
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {without.length > 0 && <p className="muted" style={{ fontSize: 12 }}>No training data yet: {without.map((h) => h.name).join(", ")}.</p>}
          <p className="muted" style={{ fontSize: 12 }}>From the Employee Workshop. Click a hotel for every measure.</p>
        </>
      )}
    </div>
  );
}
